import { UseMutation, UsePaginationMutation } from '@services/index';
import { PaginationResponse } from '@services/providers/cosem.types';
import { ColumnDef, useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { ActiveFilter } from '@utils/filters';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { readPersistedTableState, writePersistedTableState } from '@utils/tableState';
import { useDebounceValue } from 'usehooks-ts'
import { Deleting } from './MutationTable';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useDeleteMutation } from '@services/core/core/core.endpoints';

type TableVMProps<T> = {
    columns: ColumnDef<T>[];
    mutationHook: UsePaginationMutation<T>;
    exportMutationHook?: UseMutation<string>;
    defaultSorting?: OrderBy;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationHookParams?: any;
    deleting?: Deleting;
    /** Clé de persistance : recherche, page, tri et lignes par page survivent au retour sur la liste (URL + session). */
    persistKey?: string;
};

export type OrderBy = {
    field: string;
    direction: 'asc' | 'desc';
} | null;

export const useMutationTableVM = <T>({ columns, mutationHook, exportMutationHook, defaultSorting, mutationHookParams = {}, deleting, persistKey }: TableVMProps<T>) => {
    const [searchParams, setSearchParams] = useSearchParams();
    // Lu une seule fois au montage : ensuite l'état local fait foi et est recopié dans l'URL.
    const initialState = useMemo(() => readPersistedTableState(persistKey, searchParams, defaultSorting ?? null), []);
    const [data, setData] = useState<PaginationResponse<T>>();
    const [searchIsLoading, setSearchIsLoading] = useState<boolean>(false);
    const [activeFilters, setActiveFilters] = useState<ActiveFilter[] | null>(null);
    const [isLinesPerPageModalOpen, setIsLinesPerPageModalOpen] = useState<boolean>(false);
    const [linesPerPage, setLinesPerPage] = useState<number>(initialState.limit);
    const [currentPage, setCurrentPage] = useState<number>(initialState.page);
    const [orderBy, setOrderBy] = useState<OrderBy>(initialState.orderBy);
    const [getData, { isLoading, isError, isSuccess, error, data: allData }] = mutationHook();
    const [deleteEntity, { isLoading: isDeleteLoading, isSuccess: isDeleteSuccess, data: deleteData }] = useDeleteMutation();

    const [getExportData, { isLoading: isExportLoading, isError: isExportError, isSuccess: isExportSuccess, error: exportError, data: exportData }] = exportMutationHook ? exportMutationHook() : [null, { isLoading: false, isError: false, isSuccess: false, error: null, data: null }];
    const [debouncedSearch, setSearch] = useDebounceValue<string | null>(initialState.search, 500)

    const MySwal = withReactContent(Swal);


    const [isInitialMount, setIsInitialMount] = useState(true);

    const table = useReactTable({
        data: data?.results ?? [],
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
        enableRowSelection: true,
    });

    // Gestion du téléchargement du fichier Excel
    useEffect(() => {
        if (isExportSuccess && exportData) { 
            window.open(exportData.data, '_blank');      
        }
    }, [isExportSuccess, exportData]);

    useEffect(() => {
        if (isDeleteSuccess && deleteData) { 
            refresh();
        }
    }, [isDeleteSuccess, deleteData]);

    useEffect(() => {
        if (isInitialMount) {
            setIsInitialMount(false);
            return;
        }
        if (isLoading) return;
        if (isError && error) return;
        if (isSuccess && allData) {
            setSearchIsLoading(false);
            setData(allData.data);
            table.toggleAllRowsSelected(false);
        }
    }, [isLoading, isError, error, isSuccess, allData]);

    const fetchData = (params: { limit: number; page: number; search?: string | null; orderBy?: string | null; filters?: ActiveFilter[] | null }, isSearch: boolean = false) => {
        if (isSearch) {
            setSearchIsLoading(true);
        }
        getData({...params, ...mutationHookParams});
    };

    const fetchExportData = (params: { search?: string | null; orderBy?: string | null; filters?: ActiveFilter[] | null }) => {
        if (getExportData) {
            getExportData({...params, ...mutationHookParams});
        }
    };

    const handleExport = () => {
        fetchExportData({
            search: debouncedSearch,
            orderBy: orderBy ? orderBy.field + ":" + orderBy.direction : null,
            filters: activeFilters
        });
    };

    const handleDelete = (row: T & { id: string|number }) => {
        console.log(row);
        MySwal.fire({
            title: 'Êtes-vous sûr de vouloir supprimer cet élément ?',
            text: 'Cette action est irréversible',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#01539D',
            confirmButtonColor: '#01539D',
            confirmButtonText: 'Supprimer',
            cancelButtonText: 'Annuler',
        }).then((result) => {
            if (result.isConfirmed) {
                if (deleting?.entity && row.id) {
                    deleteEntity({ entity: deleting?.entity, id: row.id as string|number });
                }
            }
        })
    };

    useEffect(() => {
        if (isInitialMount) {
            return;
        }
        if (debouncedSearch && currentPage !== 1) {
            // Nouvelle recherche : on repart de la page 1, le changement de page déclenche la requête.
            setSearchIsLoading(true);
            setCurrentPage(1);
            return;
        }
        fetchData({
            limit: linesPerPage,
            page: currentPage,
            search: debouncedSearch,
            orderBy: orderBy ? orderBy.field + ":" + orderBy.direction : null,
            filters: activeFilters
        }, true);
    }, [debouncedSearch]);

    useEffect(() => {
        fetchData({
            limit: linesPerPage,
            page: currentPage,
            search: debouncedSearch,
            orderBy: orderBy ? orderBy.field + ":" + orderBy.direction : null,
            filters: activeFilters
        }, false);
    }, [linesPerPage, currentPage, orderBy]);

    useEffect(() => {
        if(!activeFilters) return;
        fetchData({
            limit: linesPerPage,
            page: 1,
            search: debouncedSearch,
            orderBy: orderBy ? orderBy.field + ":" + orderBy.direction : null,
            filters: activeFilters
        }, false);
    }, [activeFilters]);
    

    useEffect(() => {
        if (!persistKey) return;
        const next = writePersistedTableState(persistKey, searchParams, { search: debouncedSearch, page: currentPage, limit: linesPerPage, orderBy }, defaultSorting ?? null);
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
    }, [debouncedSearch, currentPage, linesPerPage, orderBy]);

    const refresh = () => fetchData({ limit: linesPerPage, page: currentPage, search: debouncedSearch, orderBy: orderBy?.field + ":" + orderBy?.direction, filters: activeFilters }, false);
    

    return {
        data: data,
        isLoading: isLoading || isDeleteLoading,
        searchIsLoading,
        error,
        isError,
        isSuccess,
        debouncedSearch,
        initialSearch: initialState.search,
        refresh,
        columns,
        setSearch,
        setActiveFilters,
        currentPage,
        setCurrentPage,
        isLinesPerPageModalOpen,
        setIsLinesPerPageModalOpen,
        linesPerPage,
        setLinesPerPage,
        orderBy,
        setOrderBy,
        table,
        isExportLoading,
        isExportError,
        exportError,
        handleExport,
        handleDelete
    };
};
